import { createSignal } from "solid-js";

type DrawerEntranceDirection = "next" | "prev" | null;

interface DrawerNavigators {
  next: () => void;
  prev: () => void;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  getPrevTodoId: (currentTodoId?: string | null) => string | null;
  getNextTodoId: (currentTodoId?: string | null) => string | null;
}

export interface FocusSlide {
  direction: "next" | "prev";
  generation: number;
}

const FOCUS_SLIDE_LIFETIME_MS = 320;
const FOCUS_SLIDE_DURATION_MS = 200;
const FOCUS_SLIDE_TRANSLATE_PX = 18;
const FOCUS_SLIDE_START_OPACITY = 0.7;
const FOCUS_SLIDE_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * Plays a brief GPU-accelerated slide animation on the focused list row to
 * mirror the direction of the drawer swipe/arrow navigation. Uses the Web
 * Animations API so repeated triggers cancel cleanly without re-mounting.
 */
export function playFocusSlideAnimation(
  el: HTMLElement | undefined,
  direction: "next" | "prev",
): void {
  if (!el || typeof el.animate !== "function") {
    return;
  }
  const fromX = direction === "next" ? FOCUS_SLIDE_TRANSLATE_PX : -FOCUS_SLIDE_TRANSLATE_PX;
  el.animate(
    [
      { transform: `translate3d(${fromX}px, 0, 0)`, opacity: FOCUS_SLIDE_START_OPACITY },
      { transform: "translate3d(0, 0, 0)", opacity: 1 },
    ],
    {
      duration: FOCUS_SLIDE_DURATION_MS,
      easing: FOCUS_SLIDE_EASING,
      fill: "none",
    },
  );
}

const [navigators, setNavigators] = createSignal<DrawerNavigators | null>(null);
const [entranceDirection, setEntranceDirection] = createSignal<DrawerEntranceDirection>(null);
const [focusSlide, setFocusSlide] = createSignal<FocusSlide | null>(null);

let focusSlideGeneration = 0;

export function registerDrawerNavigators(fns: DrawerNavigators) {
  setNavigators(fns);
}

export function clearDrawerNavigators() {
  setNavigators(null);
}

export function triggerFocusSlide(direction: "next" | "prev") {
  focusSlideGeneration += 1;
  const generation = focusSlideGeneration;
  setFocusSlide({ direction, generation });
  setTimeout(() => {
    const current = focusSlide();
    if (current?.generation === generation) {
      setFocusSlide(null);
    }
  }, FOCUS_SLIDE_LIFETIME_MS);
}

export function useDrawerSwipeNav() {
  return {
    navigators,
    entranceDirection,
    setEntranceDirection,
    focusSlide,
  };
}
